import { toNumber } from 'ezmoney';

import { type ExportAsCsvColumn } from 'src/core/utils/exportAsCsv';

import { type EnrichedEntityInformation } from '../../../types';

export type RowData = {
  /** Entity info */
  entityName: EnrichedEntityInformation['name'];
  entityCountry: EnrichedEntityInformation['country'];
  entityCurrency: EnrichedEntityInformation['currency'];
  entityStatus: 'active' | 'inactive';
  fundsStatus: 'available' | 'shortfall';

  /** Entity wallet summary */
  balance: number;
  scheduled: number;
  cards: number;
  available: number;

  /** Entity actions */

  inboxInvoicesToAssign: number;
  inboxInvoicesAwaiting: number;

  requestsAll: number;
  requestsToApprove: number;

  invoicesToReview: number;
  invoicesToSchedule: number;
  invoicesToConfirm: number;

  expenseClaimsToReview: number;
  expenseClaimsToReimburse: number;
  expenseClaimsToConfirm: number;

  receiptsLate: number;
  receiptsMissing: number;

  payablesToPrepare: number;
  payablesToExport: number;
};

export const CSV_SEPARATOR = ';';

export const columns: ExportAsCsvColumn<RowData>[] = [
  /** Entity info */
  {
    title: (t) => t('organisation.reporting.export.columns.name'),
    value: (data) => data.entityName,
  },
  {
    title: (t) => t('organisation.reporting.export.columns.country'),
    value: (data) => data.entityCountry,
  },
  {
    title: (t) => t('organisation.reporting.export.columns.currency'),
    value: (data) => data.entityCurrency,
  },
  {
    title: (t) => t('organisation.reporting.export.columns.entityStatus'),
    value: (data, t) => {
      switch (data.entityStatus) {
        case 'active':
          return t(
            'organisation.reporting.export.customValues.entityStatus.active',
          );
        case 'inactive':
          return t(
            'organisation.reporting.export.customValues.entityStatus.inactive',
          );
        default:
          return 'unknown';
      }
    },
  },
  {
    title: (t) => t('organisation.reporting.export.columns.fundsStatus'),
    value: (data, t) => {
      switch (data.fundsStatus) {
        case 'available':
          return t(
            'organisation.reporting.export.customValues.fundsStatus.available',
          );
        case 'shortfall':
          return t(
            'organisation.reporting.export.customValues.fundsStatus.shortfall',
          );
        default:
          return 'unknown';
      }
    },
  },
  /** Entity wallet summary */
  {
    title: (t) => t('organisation.reporting.export.columns.balance'),
    value: (data) =>
      new Intl.NumberFormat(undefined, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(
        toNumber({
          amount: data.balance,
          currency: data.entityCurrency,
          precision: 2,
        }),
      ),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.scheduled'),
    value: (data) =>
      new Intl.NumberFormat(undefined, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(
        toNumber({
          amount: data.scheduled,
          currency: data.entityCurrency,
          precision: 2,
        }),
      ),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.cards'),
    value: (data) =>
      new Intl.NumberFormat(undefined, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(
        toNumber({
          amount: data.cards,
          currency: data.entityCurrency,
          precision: 2,
        }),
      ),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.available'),
    value: (data) =>
      new Intl.NumberFormat(undefined, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(
        toNumber({
          amount: data.available,
          currency: data.entityCurrency,
          precision: 2,
        }),
      ),
  },
  /** Entity actions */
  {
    title: (t) =>
      t('organisation.reporting.export.columns.inboxInvoicesToAssign'),
    value: (data) => String(data.inboxInvoicesToAssign),
  },
  {
    title: (t) =>
      t('organisation.reporting.export.columns.inboxInvoicesAwaiting'),
    value: (data) => String(data.inboxInvoicesAwaiting),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.requestsAll'),
    value: (data) => String(data.requestsAll),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.requestsToApprove'),
    value: (data) => String(data.requestsToApprove),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.invoicesToReview'),
    value: (data) => String(data.invoicesToReview),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.invoicesToSchedule'),
    value: (data) => String(data.invoicesToSchedule),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.invoicesToConfirm'),
    value: (data) => String(data.invoicesToConfirm),
  },
  {
    title: (t) =>
      t('organisation.reporting.export.columns.expenseClaimsToReview'),
    value: (data) => String(data.expenseClaimsToReview),
  },
  {
    title: (t) =>
      t('organisation.reporting.export.columns.expenseClaimsToReimburse'),
    value: (data) => String(data.expenseClaimsToReimburse),
  },
  {
    title: (t) =>
      t('organisation.reporting.export.columns.expenseClaimsToConfirm'),
    value: (data) => String(data.expenseClaimsToConfirm),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.receiptsLate'),
    value: (data) => String(data.receiptsLate),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.receiptsMissing'),
    value: (data) => String(data.receiptsMissing),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.payablesToPrepare'),
    value: (data) => String(data.payablesToPrepare),
  },
  {
    title: (t) => t('organisation.reporting.export.columns.payablesToExport'),
    value: (data) => String(data.payablesToExport),
  },
];
